import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Facebook, Twitter, Linkedin, MessageCircle, CalendarDays } from "lucide-react";
import { fetchPressReleaseDetail } from "./pressReleaseApi.js";

function Breadcrumb({ title }) {
  return (
    <nav aria-label="Breadcrumb" className="absolute left-4 top-4 z-20 sm:left-8 sm:top-6">
      <ol className="flex items-center gap-2 rounded-full border border-black/10 bg-white/70 px-4 py-2 text-xs font-bold uppercase tracking-wide text-[#141419] shadow-lg shadow-black/5 backdrop-blur-md sm:text-sm">
        <li>
          <a href="/" className="inline-flex items-center gap-1.5 transition-colors duration-300 hover:text-[#DA2128]">
            Home
          </a>
        </li>
        <li className="text-black/30">&rsaquo;</li>
        <li>
          <Link to="/newsroom/press-release" className="transition-colors duration-300 hover:text-[#DA2128]">
            Press Releases
          </Link>
        </li>
        <li className="text-black/30">&rsaquo;</li>
        <li className="rounded-full bg-[#DA2128] px-3 py-1 text-white shadow-sm max-w-[160px] truncate">{title}</li>
      </ol>
    </nav>
  );
}

export default function PressReleaseDetail() {
  const { slug } = useParams();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    fetchPressReleaseDetail(slug)
      .then((data) => alive && setItem(data))
      .catch((err) => alive && setError(err.message))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [slug]);

  const shareUrl = typeof window !== "undefined" ? window.location.href : "";

  if (loading) {
    return (
      <div className="min-h-[60vh] grid place-items-center bg-cream">
        <p className="text-[13px] font-bold text-mute animate-pulse">Loading…</p>
      </div>
    );
  }

  if (error || !item) {
    return (
      <div className="min-h-[60vh] grid place-items-center bg-cream flex flex-col gap-4">
        <p className="text-[13px] font-bold text-brand">Press release not found.</p>
        <Link to="/newsroom/press-release" className="text-[13px] font-bold text-deeprose underline underline-offset-4">
          &larr; Back to Press Releases
        </Link>
      </div>
    );
  }

  return (
    <main>
      <section className="relative overflow-hidden bg-blush">
        <div className="absolute inset-0 dot-grid opacity-70" />
        <div className="absolute -top-24 -left-24 w-[420px] h-[420px] rounded-full bg-gradient-to-br from-brand/15 to-ember/10 blur-3xl" />
        <div className="absolute -bottom-32 right-[-60px] w-[460px] h-[460px] rounded-full bg-gradient-to-tr from-deeprose/15 to-ember/10 blur-3xl" />

        <div className="relative mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 pt-12 pb-14 sm:pt-16 sm:pb-16">
          <Breadcrumb title={item.title} />

          <div className="mt-14">
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55, delay: 0.08 }}
              className="inline-flex items-center gap-2 rounded-full bg-white ring-1 ring-brand/20 px-4 py-1.5 text-[11px] font-extrabold uppercase tracking-[0.2em] text-deeprose"
            >
              <CalendarDays className="w-3.5 h-3.5" />
              {new Date(item.dateISO).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}
            </motion.p>

            <motion.h1
              initial={{ opacity: 0, y: 22 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.16 }}
              className="mt-5 font-display font-bold text-[28px] leading-[1.1] sm:text-[34px] text-ink tracking-tight"
            >
              {item.title}
            </motion.h1>
          </div>
        </div>
      </section>

      <section className="bg-cream py-12 sm:py-16">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <motion.img
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            src={item.image}
            alt={item.title}
            className="w-full rounded-2xl mb-8 shadow-[0_10px_40px_-24px_rgba(180,35,24,0.35)]"
          />

          <div className="flex flex-wrap items-center gap-2 mb-8 pb-6 border-b border-ink/10">
            <span className="text-[11px] font-extrabold uppercase tracking-[0.16em] text-mute mr-1">Share —</span>
            <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full grid place-items-center ring-1 ring-ink/15 text-ink/70 hover:text-white hover:bg-gradient-to-br hover:from-crimson hover:to-deeprose hover:ring-transparent transition-all">
              <Facebook className="w-4 h-4" />
            </a>
            <a href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(item.title)}`} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full grid place-items-center ring-1 ring-ink/15 text-ink/70 hover:text-white hover:bg-gradient-to-br hover:from-crimson hover:to-deeprose hover:ring-transparent transition-all">
              <Twitter className="w-4 h-4" />
            </a>
            <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full grid place-items-center ring-1 ring-ink/15 text-ink/70 hover:text-white hover:bg-gradient-to-br hover:from-crimson hover:to-deeprose hover:ring-transparent transition-all">
              <Linkedin className="w-4 h-4" />
            </a>
            <a href={`https://api.whatsapp.com/send?text=${encodeURIComponent(item.title + " " + shareUrl)}`} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full grid place-items-center ring-1 ring-ink/15 text-ink/70 hover:text-white hover:bg-gradient-to-br hover:from-crimson hover:to-deeprose hover:ring-transparent transition-all">
              <MessageCircle className="w-4 h-4" />
            </a>
          </div>

          <article
            className="prose max-w-none prose-headings:font-display prose-headings:text-ink prose-p:text-ink/70 prose-p:leading-relaxed prose-a:text-brand"
            dangerouslySetInnerHTML={{ __html: item.content }}
          />

          <Link
            to="/newsroom/press-release"
            className="mt-12 inline-flex items-center gap-2 text-[13px] font-extrabold text-brand hover:text-deeprose transition-colors"
          >
            &larr; Back to all Press Releases
          </Link>
        </div>
      </section>
    </main>
  );
}